<template>
  <main class="mobile-i18n-page user-page">
    <div class="page-shell">
      <div class="user-locale-tools">
        <MobileAgentEntry icon-only />
        <LocaleSwitcher compact bare />
      </div>
      <!-- 用户与资产概览 -->
      <section class="overview-card" :aria-label="$t('account.userOverview')">
        <img
          class="overview-decoration"
          src="@/img/user/top-bg.png"
          alt=""
          aria-hidden="true"
        />

        <div class="profile-section">
          <component
            :is="isLogin ? 'div' : 'button'"
            class="profile-main"
            :class="{
              'is-clickable': !isLogin,
              'is-logged-out': !isLogin,
            }"
            :type="isLogin ? null : 'button'"
            @click="!isLogin && $router.push('/login')"
          >
            <span class="avatar-wrapper">
              <!-- 用户头像 -->
              <img
                class="avatar"
                :src="detail.avatar || avatarImg"
                :alt="$t('account.avatar')"
              />
            </span>
            <span class="profile-copy">
              <span class="profile-name">{{ displayName }}</span>
              <span v-if="isLogin" class="profile-meta">
                <span v-if="detail.userType" class="role-badge">
                  {{ detail.userType }}
                </span>
                <span v-if="displayLevel" class="level-badge">
                  {{ displayLevel }}
                </span>
                <!-- 申请谈判人 -->
                <button
                  v-if="showApplyBroker && !detail.userType && !displayLevel"
                  class="apply-broker-button"
                  type="button"
                  @click.stop="onJump('/applybroker')"
                >
                  {{ $t("account.applyBroker") }}
                </button>
                <!-- 已登录 -->
                <span v-else-if="!detail.userType && !displayLevel">
                  {{ $t("account.loggedIn") }}
                </span>
              </span>
            </span>
          </component>
        </div>

        <div class="assets-section" :aria-busy="balanceState === 'loading'">
          <div class="asset-grid">
            <div
              v-for="item in assetItems"
              :key="item.label"
              class="asset-item"
            >
              <strong class="m-tabular-nums" :title="devSyncText(item.value)">
                {{ item.value }}
              </strong>
              <span>{{ devSyncText(item.label) }}</span>
            </div>
          </div>

          <!-- 账户余额 -->
          <!-- 去提现 -->
          <button
            class="balance-row"
            type="button"
            :aria-label="`${$t('account.balance')}${totalBalance}，${$t(
              'account.withdraw'
            )}`"
            @click="onJump('/my/myBalance')"
          >
            <span class="balance-summary">
              <!-- 账户余额 -->
              <span class="asset-label">{{ $t("account.balance") }}</span>
              <span
                class="balance-value m-tabular-nums"
                :title="devSyncText(totalBalance)"
              >
                <small v-if="isLogin">¥</small>{{ totalBalance }}
              </span>
            </span>
            <!-- 去提现 -->
            <span class="withdraw-text">{{ $t("account.withdraw") }}</span>
            <van-icon name="arrow" />
          </button>
        </div>
      </section>

      <section class="content-card" aria-labelledby="quick-entry-title">
        <div class="section-heading">
          <!-- 快捷入口 -->
          <h2 id="quick-entry-title">{{ $t("account.quickEntry") }}</h2>
        </div>
        <div class="quick-grid">
          <!-- 即将开放 -->
          <button
            v-for="item in quickEntries"
            :key="item.label"
            class="quick-item"
            :class="{ 'is-disabled': item.disabled }"
            type="button"
            :aria-disabled="item.disabled ? 'true' : null"
            :aria-label="
              item.disabled
                ? `${item.label}，${$t('account.comingSoon')}`
                : item.label
            "
            @click="handleEntryClick(item)"
          >
            <span class="quick-icon">
              <img :src="item.img" alt="" />
              <em v-if="item.badge" class="quick-badge">
                {{ item.badge > 99 ? "99+" : item.badge }}
              </em>
            </span>
            <span class="quick-label">{{ devSyncText(item.label) }}</span>
          </button>
        </div>
      </section>

      <section
        v-if="showTradeServices"
        class="content-card service-card"
        aria-labelledby="trade-service-title"
      >
        <div class="section-heading">
          <!-- 精选交易 -->
          <h2 id="trade-service-title">{{ $t("account.featuredTrade") }}</h2>
        </div>
        <div class="service-list">
          <button
            v-for="item in tradeServices"
            :key="item.label"
            class="service-item"
            :class="{ 'is-disabled': item.disabled }"
            type="button"
            :aria-disabled="item.disabled ? 'true' : null"
            @click="handleEntryClick(item)"
          >
            <span class="service-icon"><img :src="item.img" alt="" /></span>
            <span class="service-copy">
              <strong>{{ devSyncText(item.label) }}</strong>
            </span>
            <!-- 即将开放 -->
            <span v-if="item.disabled" class="coming-soon">{{
              $t("account.comingSoon")
            }}</span>
            <van-icon v-else name="arrow" />
          </button>
        </div>
      </section>

      <!-- 退出当前账号 -->
      <button
        v-if="isLogin"
        class="logout-button"
        type="button"
        @click="logout"
      >
        {{ $t("account.logoutCurrent") }}
      </button>
    </div>
  </main>
</template>

<script>
import devSyncI18n from "@/i18n/common/index";

import { getUserInfoDetail, getUserBalance } from "@/api/user.js";
import { getKbPointsBalance } from "@/api/xunzhiKb";
import { Toast } from "vant";
import LocaleSwitcher from "@/components/common/LocaleSwitcher.vue";
import MobileAgentEntry from '@/components/chat-workbench/MobileAgentEntry.vue';

export default {
  mixins: [devSyncI18n],
  name: "MobileUserPage",
  components: { LocaleSwitcher, MobileAgentEntry },
  data() {
    return {
      avatarImg: require("@/assets/default-avatar.png"),
      detail: {},
      balance: {
        totalBalance: 0,
        frozenBalance: 0,
        withdrawableBalance: 0,
        jf: 0,
        jb: 0,
      },
      balanceState: "idle",
    };
  },
  created() {
    if (this.isLogin) this.getUserInfoDetail();
  },
  computed: {
    isLogin() {
      if (this.$route.meta.userPreviewLoggedOut) return false;
      return Boolean(this.$store.getters.token);
    },
    unreadCount() {
      return Number(this.$store.state.user.unreadCount || 0);
    },
    displayName() {
      if (!this.isLogin) return "请登录";
      return (
        this.detail.authnName ||
        this.detail.nick_name ||
        this.detail.mobile ||
        "芯链用户"
      );
    },
    displayLevel() {
      if (this.detail.businessType === 10 || this.detail.businessType === 20) {
        return this.detail.goodlevel ?? "好评等级1";
      }
      return "";
    },
    showApplyBroker() {
      return (
        this.isLogin &&
        Number(this.detail.businessType) === 0 &&
        this.detail.shopStatus == null
      );
    },
    showTradeServices() {
      return (
        this.isLogin &&
        (this.hasShipmentPermission || this.detail.businessType != 0)
      );
    },
    hasShipmentPermission() {
      return [1, 2, 3, 4, 6, 7].includes(Number(this.detail.privateRole));
    },
    hasGoodsLibraryPermission() {
      // 与 PC 个人中心侧栏一致：销售商务、采购商务、商务经理可见。
      return [4, 5, 6].includes(Number(this.detail.privateRole));
    },
    totalBalance() {
      return this.displayBalance(["totalBalance"]);
    },
    assetItems() {
      return [
        {
          // 可提现
          label: this.$t("account.withdrawable"),
          value: this.displayBalance(["withdrawableBalance"]),
        },
        {
          // 冻结中
          label: this.$t("account.frozen"),
          value: this.displayBalance(["frozenBalance"]),
        },
        {
          // 积分
          label: this.$t("account.points"),
          value: this.displayBalance(["jf"]),
        },
        {
          // 金币
          label: this.$t("account.coins"),
          value: this.displayBalance(["jb"]),
        },
      ];
    },
    quickEntries() {
      return [
        {
          // 账户余额
          label: this.$t("account.balance"),
          img: require("@/img/user/wallet.png"),
          path: "/my/myBalance",
        },
        {
          // 交易订单
          label: this.$t("account.tradeOrders"),
          img: require("@/img/user/order.png"),
          path: "/myxg/myTransactionOrder",
        },
        {
          // 商品中心
          label: this.$t("account.goodsCenter"),
          img: require("@/img/user/goods.png"),
          path: "/myxg/myGoods",
        },
        {
          label: this.$t("accountSettings.personal.title"),
          img: require("@/img/user/message.png"),
          path: "/my/personal-info",
        },
        {
          // 消息管理
          label: this.$t("account.messages"),
          img: require("@/img/user/message.png"),
          path: "/myxg/messageCenter",
          badge: this.unreadCount,
        },
      ];
    },
    tradeServices() {
      const services = [
        {
          // 交易订单
          label: this.$t("account.tradeOrders"),
          img: require("@/img/user/order.png"),
          path: "/myxg/myTransactionOrder",
          disabled: false,
        },
        {
          // 商品中心
          label: this.$t("account.goodsCenter"),
          img: require("@/img/user/goods.png"),
          path: "/myxg/myGoods",
          disabled: false,
        },
        {
          // 消息管理
          label: this.$t("account.messages"),
          img: require("@/img/user/comment.png"),
          path: "/myxg/messageCenter",
          disabled: false,
        },
        {
          // 出货管理
          label: this.$t("personalMenu.outboundManagement"),
          img: require("@/img/prodetail/hwqk.png"),
          path: "/follow/outboundOrderList",
          disabled: false,
        },
      ];

      if (this.hasGoodsLibraryPermission) {
        services.splice(
          2,
          0,
          {
            // 商品库
            label: this.$t("account.goodsLibrary"),
            img: require("@/img/user/goods.png"),
            path: "/myxg/goodsLibrary",
            disabled: false,
          },
          {
            // 供应商管理
            label: this.$t("account.suppliers"),
            img: require("@/img/user/company.png"),
            path: "/myxg/supplierList",
            disabled: false,
          }
        );
      }
      return services.filter(
        (item) =>
          item.path !== "/follow/outboundOrderList" ||
          this.hasShipmentPermission
      );
    },
  },
  methods: {
    displayBalance(keys) {
      if (!this.isLogin) return "--";
      if (this.balanceState !== "success") return "--";
      for (const key of keys) {
        const value = this.balance[key];
        if (value !== undefined && value !== null && value !== "") {
          return this.$formatPrice(value);
        }
      }
      return this.$formatPrice(0);
    },
    formatNumber(value, formatOptions = {}) {
      const numericValue = Number(String(value).replace(/,/g, ""));
      if (!Number.isFinite(numericValue)) return String(value);
      return numericValue.toLocaleString(
        this.$store.state.locale.uiLocale,
        formatOptions
      );
    },
    async getUserInfoDetail() {
      this.balanceState = "loading";
      const [userInfoResult, balanceResult, pointsResult] =
        await Promise.allSettled([
          getUserInfoDetail(),
          getUserBalance(),
          getKbPointsBalance(),
        ]);

      if (
        userInfoResult.status === "fulfilled" &&
        userInfoResult.value.code === 200
      ) {
        this.detail = userInfoResult.value.data.userInfo || {};
        this.$store.commit("user/setUserDetails", this.detail);
        void this.$store.dispatch("user/refreshUnreadCount").catch(() => console.warn("消息未读汇总刷新失败"));
      }

      if (
        balanceResult.status === "fulfilled" &&
        balanceResult.value.code === 200
      ) {
        // 与 PC 端一致：商城余额保留，积分不读取商城旧 jf 字段。
        this.balance = { ...balanceResult.value.data, jf: 0 };
        this.balanceState = "success";
      } else {
        this.balanceState = "error";
      }

      if (pointsResult.status === "fulfilled") {
        const encyclopediaPoints = Number(pointsResult.value?.data?.balance);
        this.balance = {
          ...this.balance,
          jf: Number.isFinite(encyclopediaPoints) ? encyclopediaPoints : 0,
        };
      } else {
        console.warn("获取百科积分余额失败", pointsResult.reason);
      }
    },
    handleEntryClick(item) {
      if (!this.isLogin) {
        // 请先登录
        Toast.fail(this.$t("common.loginRequired"));
        return;
      }
      if (item.disabled || !item.path) {
        // 暂未开放
        Toast(item.disabledReason || this.$t("sourcePublish.notAvailable"));
        return;
      }
      this.$router.push(item.path);
    },
    onJump(path) {
      if (!this.isLogin) {
        // 请先登录
        Toast.fail(this.$t("common.loginRequired"));
        return;
      }
      this.$router.push(path);
    },
    logout() {
      this.$dialog
        // 温馨提示
        // 确认退出当前账号吗？
        .confirm({
          // 温馨提示
          title: this.$t("mobile.product.notice"),
          // 确认退出当前账号吗？
          message: this.$t("account.confirmLogout"),
        })
        .then(() => {
          sessionStorage.removeItem("hasTip");
          this.$store.dispatch("user/logout");
          this.detail = {};
          this.balance = {
            totalBalance: 0,
            frozenBalance: 0,
            withdrawableBalance: 0,
            jf: 0,
            jb: 0,
          };
          this.balanceState = "idle";
          this.$router.replace("/home");
        })
        .catch(() => {});
    },
  },
};
</script>

<style lang="less">
@import "../../styles/mobile-design-tokens.less";
</style>

<style lang="less" scoped>
// 基础样式与页面变量
.user-page,
.user-page * {
  box-sizing: border-box;
}

button {
  font: inherit;
}

.user-page {
  --user-page-bg: #f6f8fb;
  --user-icon-bg: #f5f7fa;
  --user-balance-bg: linear-gradient(135deg, #e8f1ff 0%, #eef3fa 100%);
  --user-balance-active: linear-gradient(135deg, #dfeafd 0%, #e7edf6 100%);
  --user-card-shadow: 0 4px 16px rgba(15, 23, 42, 0.04);
  width: 100%;
  min-height: 100vh;
  color: var(--m-text-primary);
  background: var(--user-page-bg);
  font-family: var(--m-font-family);
}

// 页面容器与通用卡片
.page-shell {
  width: 100%;
  max-width: var(--m-content-max-width);
  min-height: 100vh;
  margin: 0 auto;
  padding: calc(env(safe-area-inset-top) + 16px) var(--m-page-gutter)
    calc(76px + env(safe-area-inset-bottom));

  .user-locale-tools {
    align-items: center;
    gap: 4px;
    position: relative;
    // 高于资产区的局部层级，保证语言菜单完整覆盖下方卡片。
    z-index: 3;
    display: flex;
    justify-content: flex-end;
    min-height: 44px;
    margin-bottom: var(--m-space-2);
  }
}

.overview-card,
.content-card {
  position: relative;
  overflow: hidden;
  border: 0;
  border-radius: var(--m-radius-lg);
  background: var(--m-surface);
  box-shadow: var(--user-card-shadow);
}

.content-card {
  margin-top: var(--m-space-3);
  padding: var(--m-space-3) var(--m-space-4);
}

.section-heading {
  margin-bottom: var(--m-space-3);

  h2 {
    margin: 0;
    color: var(--m-text-strong);
    font-size: 15px;
    font-weight: 600;
    line-height: 22px;
  }
}

// 用户信息区域
.overview-card {
  padding: var(--m-space-4);
}

.overview-decoration {
  position: absolute;
  z-index: 0;
  top: -8px;
  right: 0;
  width: 152px;
  opacity: 0.18;
  pointer-events: none;
}

.profile-section {
  position: relative;
  z-index: 1;
  display: flex;
  min-height: 52px;
  align-items: center;
  gap: var(--m-space-2);
}

.profile-main {
  display: flex;
  min-width: 0;
  min-height: var(--m-touch-target);
  flex: 1;
  align-items: center;
  padding: 0;
  border: 0;
  color: inherit;
  background: transparent;
  text-align: left;
  transform: translateY(var(--m-space-1));

  &.is-clickable:active {
    opacity: 0.72;
  }

  &.is-logged-out {
    &::after,
    > .van-icon,
    > .login-arrow {
      display: none !important;
      content: none !important;
    }
  }
}

.avatar-wrapper {
  width: 48px;
  height: 48px;
  flex: 0 0 48px;
  overflow: hidden;
  border: 3px solid rgba(255, 255, 255, 0.94);
  border-radius: 50%;
  background: var(--m-surface-muted);
  box-shadow: 0 3px 10px rgba(36, 109, 224, 0.1);
}

.avatar {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.profile-copy {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  margin-left: var(--m-space-2);
}

.profile-name {
  overflow: hidden;
  color: var(--m-text-strong);
  font-size: 16px;
  font-weight: 600;
  line-height: 23px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.profile-meta {
  display: flex;
  min-height: 20px;
  align-items: center;
  gap: 4px;
  margin-top: 2px;
  color: var(--m-text-secondary);
  font-size: var(--m-font-micro);
  line-height: var(--m-line-micro);
  overflow: hidden;
}

.role-badge,
.level-badge {
  display: inline-flex;
  min-height: 18px;
  align-items: center;
  padding: 0 6px;
  border-radius: var(--m-radius-full);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--m-brand-700);
  background: var(--m-brand-50);
}

.apply-broker-button {
  display: inline-flex;
  min-height: 18px;
  align-items: center;
  padding: 0 6px;
  border: 1px solid var(--m-brand-200);
  border-radius: var(--m-radius-full);
  color: var(--m-brand-700);
  background: var(--m-brand-50);
  font-size: var(--m-font-micro);
  font-weight: 500;
  line-height: var(--m-line-micro);

  &:active {
    background: var(--m-brand-100);
  }
}

// 资产与余额区域
.assets-section {
  position: relative;
  z-index: 1;
  margin: var(--m-space-6) calc(0px - var(--m-space-4))
    calc(0px - var(--m-space-4));
  border-radius: 0 0 var(--m-radius-lg) var(--m-radius-lg);
  background: var(--user-balance-bg);
}

.balance-value {
  min-width: 0;
  color: var(--m-brand-700);
  font-size: clamp(12px, 3.5vw, 13px);
  font-weight: 500;
  line-height: 18px;
  white-space: nowrap;

  small {
    margin-right: 2px;
    font-size: 13px;
    font-weight: 500;
  }
}

.balance-summary {
  display: flex;
  min-width: 0;
  align-items: baseline;
  gap: var(--m-space-2);
}

.asset-grid {
  position: relative;
  z-index: 2;
  display: grid;
  gap: var(--m-space-1);
  grid-template-columns: repeat(4, minmax(0, 1fr));
  padding: 0 var(--m-space-3) var(--m-space-4);
  border-radius: 0 0 18px 18px;
  background: var(--m-surface);

  &::after {
    position: absolute;
    bottom: -7px;
    left: 50%;
    width: 96px;
    height: 8px;
    border-radius: 0 0 50% 50% / 0 0 100% 100%;
    background: var(--m-surface);
    clip-path: path(
      "M 0 0 C 28 0 37 1 43 5 C 46 7 47 8 48 8 C 49 8 50 7 53 5 C 59 1 68 0 96 0 Z"
    );
    content: "";
    transform: translateX(-50%);
  }
}

.asset-item {
  display: flex;
  min-width: 0;
  align-items: center;
  flex-direction: column;
  padding: 0 4px;

  strong {
    width: 100%;
    color: var(--m-text-strong);
    font-size: 13px;
    font-weight: 600;
    text-align: center;
    white-space: nowrap;
  }

  span {
    margin-top: 2px;
    color: var(--m-text-secondary);
    font-size: var(--m-font-micro);
    line-height: var(--m-line-micro);
  }
}

.balance-row {
  position: relative;
  z-index: 1;
  display: grid;
  width: 100%;
  min-height: 40px;
  grid-template-columns: minmax(0, 1fr) auto 14px;
  align-items: center;
  gap: var(--m-space-2);
  padding: 2px var(--m-space-4) 0;
  border: 0;
  border-radius: 0 0 var(--m-radius-lg) var(--m-radius-lg);
  color: inherit;
  background: transparent;
  box-shadow: inset 0 -1px 0 rgba(36, 109, 224, 0.06);
  text-align: left;

  &:active {
    background: var(--user-balance-active);
  }

  .asset-label {
    color: var(--m-text-secondary);
    font-size: 13px;
    line-height: 18px;
  }

  > .van-icon {
    margin-left: calc(0px - var(--m-space-1));
    color: var(--m-text-secondary);
    font-size: 14px;
  }
}

.withdraw-text {
  color: var(--m-text-secondary);
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
  white-space: nowrap;
}

// 快捷入口
.quick-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--m-space-1);
}

.quick-item {
  display: flex;
  min-width: 0;
  min-height: 72px;
  align-items: center;
  flex-direction: column;
  justify-content: flex-start;
  padding: 4px 2px 6px;
  border: 0;
  border-radius: var(--m-radius-md);
  color: var(--m-text-primary);
  background: transparent;

  &:active:not(.is-disabled) {
    background: var(--m-brand-50);
  }

  &.is-disabled {
    background: transparent;

    .quick-icon,
    .quick-label {
      opacity: 0.52;
    }
  }
}

.quick-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 36px;
  height: 36px;
  border-radius: var(--m-radius-md);
  background: var(--user-icon-bg);

  img {
    width: 22px;
    height: 22px;
    filter: grayscale(1);
    opacity: 0.9;
    object-fit: contain;
  }

  .quick-badge {
    position: absolute;
    top: -5px;
    right: -8px;
    min-width: 17px;
    height: 17px;
    padding: 0 4px;
    color: #ffffff;
    font-size: 9px;
    font-style: normal;
    line-height: 17px;
    text-align: center;
    background: #ee4d4d;
    border: 2px solid #ffffff;
    border-radius: 10px;
  }
}

.quick-label {
  max-width: 100%;
  margin-top: var(--m-space-1);
  overflow: hidden;
  font-size: var(--m-font-caption);
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
}

// 精选交易服务
.service-card {
  padding-bottom: 8px;
}

.service-list {
  margin: 0;
}

.service-item {
  display: flex;
  width: 100%;
  min-height: 52px;
  align-items: center;
  padding: 6px 0;
  border: 0;
  color: inherit;
  background: transparent;
  text-align: left;

  &:active:not(.is-disabled) {
    background: var(--m-surface-muted);
  }

  > .van-icon {
    flex: 0 0 auto;
    margin-left: 8px;
    color: var(--m-text-placeholder);
    font-size: 14px;
  }
}

.service-icon {
  display: inline-flex;
  width: 24px;
  height: 24px;
  flex: 0 0 24px;
  align-items: center;
  justify-content: center;
  border-radius: 0;
  background: transparent;

  img {
    width: 22px;
    height: 22px;
    filter: grayscale(1);
    opacity: 0.9;
    object-fit: contain;
  }
}

.service-copy {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  margin-left: 8px;

  strong {
    color: var(--m-text-strong);
    font-size: 13px;
    font-weight: 500;
    line-height: var(--m-line-body);
  }
}

.coming-soon {
  flex: 0 0 auto;
  margin-left: 8px;
  padding: 1px 6px;
  border-radius: var(--m-radius-full);
  color: var(--m-text-secondary);
  background: var(--m-surface-muted);
  font-size: var(--m-font-micro);
  line-height: var(--m-line-micro);
}

// 退出登录与交互状态
.logout-button {
  width: 100%;
  min-height: var(--m-touch-target);
  margin-top: var(--m-space-3);
  border: 0;
  border-radius: var(--m-radius-md);
  color: var(--m-text-secondary);
  background: var(--m-surface);
  box-shadow: var(--user-card-shadow);
  font-size: var(--m-font-body);
  font-weight: 500;

  &:active {
    color: var(--m-danger);
    background: var(--m-danger-bg);
  }
}

.profile-main:focus-visible,
.apply-broker-button:focus-visible,
.balance-row:focus-visible,
.quick-item:focus-visible,
.service-item:focus-visible,
.logout-button:focus-visible {
  outline: 0;
  box-shadow: var(--m-focus-ring);
}

// 小屏适配
@media (max-width: 359px) {
  .profile-name {
    font-size: 15px;
  }

  .quick-grid {
    gap: var(--m-space-1);
  }

  .quick-label {
    font-size: var(--m-font-micro);
  }
}
</style>
